import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { useBrandingColors } from '../hooks/useBrandingColors';

/**
 * Модалка результатов проверки кандидатов перед рассылкой (TASKNEIROKLYUCH-434).
 * Показывает: сколько добавлено, сколько доступно, список заблокированных.
 */
/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   onConfirm: () => void;
 *   loading?: boolean;
 *   checkResult?: any;
 *   message?: string | null;
 *   messageType?: 'warning' | 'error';
 *   title?: string;
 *   blockedTitle?: string;
 *   confirmLabel?: string | null;
 *   showCancel?: boolean;
 * }} props
 */
const CampaignCheckResultsModal = ({
  open,
  onClose,
  onConfirm,
  loading,
  checkResult,
  message = null,
  messageType = 'warning',
  title = 'Проверка кандидатов',
  blockedTitle = 'Кандидаты в активном процессинге:',
  confirmLabel = null,
  showCancel = true,
}) => {
  const colors = useBrandingColors();

  if (!checkResult) return null;

  const { total, available, blocked = [] } = checkResult;
  const hasAvailable = available > 0;
  // TASKNEIROKLYUCH-434: отличаем «реально в процессинге» (source === 'estaff') от
  // «не удалось проверить — сервис недоступен» (source === 'estaff_error').
  // Backend уже проставляет source; здесь только визуально разделяем.
  const hasServiceErrors = blocked.some((item) => item.source === 'estaff_error');

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full"
                    style={{ backgroundColor: blocked.length > 0 ? '#FEF3C7' : `${colors.primary}20` }}>
                    {blocked.length > 0 ? (
                      <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" aria-hidden="true" />
                    ) : (
                      <EnvelopeIcon className="h-6 w-6" style={{ color: colors.primary }} aria-hidden="true" />
                    )}
                  </div>
                  <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {title}
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="text-sm text-gray-900">
                        Добавлено <span className="font-semibold">{total}</span> кандидатов,
                        доступны для диалога <span className="font-semibold" style={{ color: hasAvailable ? colors.primary : '#EF4444' }}>{available}</span>
                      </p>
                    </div>

                    {hasServiceErrors && (
                      <div
                        className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-3 text-left"
                        role="alert"
                        data-testid="estaff-unavailable-hint"
                      >
                        <p className="text-sm text-orange-800">
                          <span className="font-medium">⚠️ Сервис E-Staff временно недоступен.</span>{' '}
                          Часть кандидатов помечена как «не удалось проверить» — их статус
                          блокировки не подтверждён. Попробуйте повторить позже.
                        </p>
                      </div>
                    )}

                    {message && (
                      <div
                        className={`mt-4 rounded-lg border p-3 text-left ${
                          messageType === 'warning'
                            ? 'border-yellow-200 bg-yellow-50 text-yellow-800'
                            : 'border-red-200 bg-red-50 text-red-700'
                        }`}
                        role="alert"
                        data-testid={`campaign-check-message-${messageType}`}
                      >
                        <p className="text-sm">{message}</p>
                      </div>
                    )}

                    {blocked.length > 0 && (
                      <div className="mt-4 text-left">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {blockedTitle}
                        </p>
                        <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                          {blocked.map((item, index) => {
                            const isServiceError = item.source === 'estaff_error';
                            const rowClass = isServiceError
                              ? 'bg-orange-50'
                              : index % 2 === 0
                                ? 'bg-gray-50'
                                : 'bg-white';
                            return (
                              <div
                                key={index}
                                className={`px-3 py-2 text-sm ${rowClass}`}
                                data-testid={isServiceError ? 'blocked-service-error' : 'blocked-real'}
                              >
                                <span className="font-medium text-gray-900">{item.name}</span>
                                <span className={`ml-2 ${isServiceError ? 'text-orange-700' : 'text-gray-500'}`}>
                                  — {isServiceError
                                    ? 'не удалось проверить (сервис недоступен)'
                                    : item.reason}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className={`mt-5 sm:mt-6 ${showCancel ? 'sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3' : ''}`}>
                  <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-xl border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${showCancel ? 'sm:col-start-2' : ''}`}
                    style={{ backgroundColor: hasAvailable ? colors.primary : '#9CA3AF' }}
                    onMouseEnter={(e) => { if (hasAvailable && !loading) e.currentTarget.style.backgroundColor = colors.primaryHover; }}
                    onMouseLeave={(e) => { if (hasAvailable) e.currentTarget.style.backgroundColor = colors.primary; }}
                    onClick={onConfirm}
                    disabled={loading || !hasAvailable}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Создание...
                      </>
                    ) : (
                      <>
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        {confirmLabel || `Запустить рассылку (${available})`}
                      </>
                    )}
                  </button>
                  {showCancel && (
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none sm:col-start-1 sm:mt-0 sm:text-sm"
                      onClick={onClose}
                      disabled={loading}
                    >
                      Отменить
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CampaignCheckResultsModal;
